import { useCallback, useRef, type PointerEvent as ReactPointerEvent } from 'react'
import { useInstantLayoutTransition } from 'framer-motion'

import { useSetSidepanelGeometry } from '../atoms'
import { isDragExcluded } from '../drag-exclusion'
import { sidepanelDragControlsRef } from './use-sidepanel-drag'

/** Second toolbar click within this window can re-center — unless it becomes a drag. */
const TOOLBAR_DOUBLE_CLICK_MS = 500
/** Clicks farther apart than this are not a double-click (matches drag activation). */
const TOOLBAR_DOUBLE_CLICK_DISTANCE_PX = 6

/**
 * Drag-to-reposition from a toolbar-height band, shared by the header's toolbar
 * row and the quick tour's drag strip.
 *
 * Interactive children (buttons, menus, inputs) keep their clicks. A drag only
 * starts from inert areas, and only once the pointer passes the activation
 * threshold. A second click in the same spot re-centers on release (title-bar
 * style), but holding that second click and dragging cancels the center.
 */
export function useToolbarDragSurface() {
  const setGeometry = useSetSidepanelGeometry()
  const startInstantLayout = useInstantLayoutTransition()
  const lastTapRef = useRef<{ t: number; x: number; y: number } | null>(null)

  return useCallback(
    (e: ReactPointerEvent) => {
      if (e.button !== 0 || isDragExcluded(e.target)) {
        return
      }

      const now = performance.now()
      const last = lastTapRef.current
      const dx = last ? e.clientX - last.x : 0
      const dy = last ? e.clientY - last.y : 0
      const wantsCenter =
        !!last &&
        now - last.t <= TOOLBAR_DOUBLE_CLICK_MS &&
        dx * dx + dy * dy <= TOOLBAR_DOUBLE_CLICK_DISTANCE_PX * TOOLBAR_DOUBLE_CLICK_DISTANCE_PX

      sidepanelDragControlsRef.current?.start(e, {
        onSettle: ({ activated }) => {
          lastTapRef.current = { t: now, x: e.clientX, y: e.clientY }
          if (wantsCenter && !activated) {
            startInstantLayout(() => setGeometry({ verticalOffset: null }))
          }
        },
      })
    },
    [startInstantLayout, setGeometry],
  )
}
